import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import {
  EnrichedMeterData,
  DeviceEntry,
  TelemetryApiResponse,
  transformApiResponse,
  aggregateEnergyData,
  analyzePowerQuality,
  calculatePeakDemand,
  analyzeOperations,
  analyzeTamper,
} from "../utils/dataUtils";
import { fetchGETRequest } from "../utils/services";

// Telemetry keys to request
const TELEMETRY_KEYS = [
  "IP_KWH",
  "IP_MeterTime",
  "IP_Current",
  "IP_Voltage",
  "IP_Frequency",
  "IP_NeutralCurrent",
  "IP_PowerFactor",
  "IP_KW",
  "IP_KVA",
  "IP_KWHEXP",
  "IP_KVAH",
  "IP_KVAHEXP",
  "IP_MDKW",
  "IP_MDKVA",
  "IP_MDResetCount",
  "IP_ProgramCount",
  "IP_TamperCount",
  "IP_TotalPowerOnDuration",
  "IP_RelayStatus",
  "IP_Relay_Status",
  "IP_Relay_Change",
  "IP_RelayChange",
  "IP_ConnectedCount",
  "IP_DisconnectedCount",
  "IP_RelayChangeCount",
  "IP_Tamper_Delta",
  "IP_Reset_Delta",
  "IP_Program_Delta",
  "IP_LoadLimitValue",
  "EventCode",
  "EventDescription",
].join(",");

interface ReportDataContextType {
  data: EnrichedMeterData[];
  loading: boolean;
  error: string | null;
  progress: string;
  meterCount: number;
  meterNames: string[];
  reports: {
    energy: ReturnType<typeof aggregateEnergyData>;
    powerQuality: ReturnType<typeof analyzePowerQuality>;
    demand: ReturnType<typeof calculatePeakDemand>;
    operations: ReturnType<typeof analyzeOperations>;
    tamper: ReturnType<typeof analyzeTamper>;
  } | null;
  dateRange: { from: Date; to: Date };
  refreshData: () => Promise<void>;
}

const ReportDataContext = createContext<ReportDataContextType | undefined>(
  undefined,
);

interface ReportDataProviderProps {
  children: ReactNode;
  customerId: string;
  dateRange: { from: Date; to: Date };
}

export const ReportDataProvider = ({
  children,
  customerId,
  dateRange,
}: ReportDataProviderProps) => {
  const [data, setData] = useState<EnrichedMeterData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>("");
  const [meterCount, setMeterCount] = useState<number>(0);
  const [meterNames, setMeterNames] = useState<string[]>([]);
  const [meterStatuses, setMeterStatuses] = useState<Record<string, boolean>>(
    {},
  );

  const fetchData = useCallback(async () => {
    if (!customerId || !customerId.trim()) {
      setData([]);
      setError(null);
      setMeterCount(0);
      setMeterNames([]);
      return;
    }

    setLoading(true);
    setError(null);
    setProgress("Fetching meter list...");

    try {
      // Step 1: Fetch all meters for the customer
      const devicesResponse = await fetchGETRequest(
        `customer/${customerId}/devices?pageSize=50&page=0`,
        {},
        true,
      );

      const devices: DeviceEntry[] = devicesResponse?.data?.data || [];

      if (devices.length === 0) {
        setError("No meters found for this customer.");
        setData([]);
        setMeterCount(0);
        setMeterNames([]);
        setLoading(false);
        return;
      }

      const meterList = devices.map((d: any) => ({
        id: d.id?.id,
        name: d.name || d.label || d.id?.id,
      }));

      setMeterCount(meterList.length);
      setMeterNames(meterList.map((m: any) => m.name));
      setProgress(
        `Found ${meterList.length} meters. Fetching telemetry data...`,
      );

      // Step 2: Fetch telemetry data for each meter
      const startTs = dateRange.from.getTime();
      const endTs = dateRange.to.getTime();

      const allRows: EnrichedMeterData[] = [];
      let completedCount = 0;

      // Process meters in batches of 5 to avoid overwhelming the API
      const BATCH_SIZE = 5;
      for (
        let batchStart = 0;
        batchStart < meterList.length;
        batchStart += BATCH_SIZE
      ) {
        const batch = meterList.slice(batchStart, batchStart + BATCH_SIZE);

        const statuses: Record<string, boolean> = {};

        const batchResults = await Promise.all(
          batch.map(async (meter: any) => {
            try {
              const [telemetryRes, statusRes] = await Promise.all([
                fetchGETRequest<TelemetryApiResponse>(
                  `plugins/telemetry/DEVICE/${meter.id}/values/timeseries?keys=${TELEMETRY_KEYS}&startTs=${startTs}&endTs=${endTs}&intervalType=WEEK`,
                  {},
                  true,
                ),
                fetchGETRequest<{ Status: string }>(
                  `devices/${meter.id}/connectionstatus`,
                  {},
                  true,
                ),
              ]);

              const apiData = telemetryRes?.data;
              const isActive =
                statusRes?.data?.Status?.toLowerCase() === "online";

              statuses[meter.name] = isActive;

              if (apiData && Object.keys(apiData).length > 0) {
                return transformApiResponse(
                  meter.id,
                  meter.name,
                  apiData,
                  isActive,
                );
              }
              return [];
            } catch (err) {
              console.warn(
                `Failed to fetch data for meter ${meter.name}:`,
                err,
              );
              return [];
            }
          }),
        );

        setMeterStatuses((prev) => ({ ...prev, ...statuses }));
        batchResults.forEach((rows) => allRows.push(...rows));
        completedCount += batch.length;
        setProgress(
          `Fetched ${completedCount}/${meterList.length} meters (${allRows.length} readings)...`,
        );
      }

      if (allRows.length === 0) {
        setError(
          "No telemetry data found for the selected date range. Try a different range.",
        );
      }

      setData(allRows);
      setProgress(
        `Loaded ${allRows.length} readings from ${meterList.length} meters`,
      );
    } catch (err: any) {
      console.error("Error fetching report data:", err);
      setError(
        err?.message ||
          "Failed to fetch report data. Please check the Customer ID and try again.",
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [customerId, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Compute derived state for reports
  const reports = useMemo(() => {
    if (data.length === 0) return null;

    const totalMeters = meterCount;
    const totalConnected = Object.values(meterStatuses).filter(
      (s) => s === true,
    ).length;

    return {
      energy: aggregateEnergyData(data),
      powerQuality: analyzePowerQuality(data),
      demand: calculatePeakDemand(data),
      operations: analyzeOperations(
        data,
        totalConnected,
        totalMeters,
        meterStatuses,
      ),
      tamper: analyzeTamper(data),
    };
  }, [data, meterCount, meterStatuses]);

  return (
    <ReportDataContext.Provider
      value={{
        data,
        loading,
        error,
        progress,
        meterCount,
        meterNames,
        reports,
        dateRange,
        refreshData: fetchData,
      }}
    >
      {children}
    </ReportDataContext.Provider>
  );
};

export const useReportData = () => {
  const context = useContext(ReportDataContext);
  if (context === undefined) {
    throw new Error("useReportData must be used within a ReportDataProvider");
  }
  return context;
};
